import React, { useState, useRef, useEffect } from 'react'
import { useTheme } from '../../hooks/useTheme'

interface PlanoMapperProps {
  planoImageUrl: string
  espacioCodigo: string
  onCoordinatesChange: (coords: {
    plano_x: number | null
    plano_y: number | null
    plano_radius: number | null
    plano_ancho: number | null
    plano_alto: number | null
    plano_vertices: Array<{ x: number; y: number }> | null
  }) => void
  initialValues?: {
    plano_x: number | null
    plano_y: number | null
    plano_radius: number | null
    plano_ancho: number | null
    plano_alto: number | null
    plano_vertices?: Array<{ x: number; y: number }> | null
  }
  defaultExpanded?: boolean
}

type ShapeType = 'circular' | 'rectangular' | 'polygon'

const PlanoMapper: React.FC<PlanoMapperProps> = ({
  planoImageUrl,
  espacioCodigo,
  onCoordinatesChange,
  defaultExpanded = false,
  initialValues = {
    plano_x: null,
    plano_y: null,
    plano_radius: null,
    plano_ancho: null,
    plano_alto: null,
    plano_vertices: null
  }
}) => {
  const { colors } = useTheme()
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [shapeType, setShapeType] = useState<ShapeType>(
    initialValues.plano_vertices ? 'polygon' :
    initialValues.plano_radius ? 'circular' : 'rectangular'
  )

  // Coordenadas
  const [planoX, setPlanoX] = useState(initialValues.plano_x || 0)
  const [planoY, setPlanoY] = useState(initialValues.plano_y || 0)
  const [radius, setRadius] = useState(initialValues.plano_radius || 20)
  const [ancho, setAncho] = useState(initialValues.plano_ancho || 60)
  const [alto, setAlto] = useState(initialValues.plano_alto || 50)
  const [polygonVertices, setPolygonVertices] = useState<Array<{ x: number; y: number }>>(
    initialValues.plano_vertices || []
  )

  const [isDragging, setIsDragging] = useState(false)
  const [resizingCorner, setResizingCorner] = useState<string | null>(null)

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    })
  }

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef.current || !containerRef.current) return

    const imgRect = imgRef.current.getBoundingClientRect()
    const containerRect = containerRef.current.getBoundingClientRect()

    // Calcular posición relativa a la imagen
    const x = e.clientX - imgRect.left
    const y = e.clientY - imgRect.top

    // Convertir a coordenadas de la imagen original (no escalada)
    const scaleX = imageDimensions.width / imgRect.width
    const scaleY = imageDimensions.height / imgRect.height

    const actualX = Math.round(x * scaleX)
    const actualY = Math.round(y * scaleY)

    // Si es polígono, agregar vértices
    if (shapeType === 'polygon') {
      if (polygonVertices.length < 4) {
        setPolygonVertices([...polygonVertices, { x: actualX, y: actualY }])
      }
    } else {
      setPlanoX(actualX)
      setPlanoY(actualY)
    }
  }

  // Notificar cambios al padre (solo cuando las coordenadas cambian, no cuando cambia la función)
  useEffect(() => {
    if (shapeType === 'polygon') {
      if (polygonVertices.length > 0) {
        onCoordinatesChange({
          plano_x: null,
          plano_y: null,
          plano_radius: null,
          plano_ancho: null,
          plano_alto: null,
          plano_vertices: polygonVertices.length === 4 ? polygonVertices : null
        })
      }
    } else if (planoX !== null && planoX !== undefined && planoY !== null && planoY !== undefined) {
      onCoordinatesChange({
        plano_x: planoX,
        plano_y: planoY,
        plano_radius: shapeType === 'circular' ? radius : null,
        plano_ancho: shapeType === 'rectangular' ? ancho : null,
        plano_alto: shapeType === 'rectangular' ? alto : null,
        plano_vertices: null
      })
    }
  }, [planoX, planoY, radius, ancho, alto, shapeType, polygonVertices])

  const handleClear = () => {
    setPlanoX(0)
    setPlanoY(0)
    setRadius(20)
    setAncho(60)
    setAlto(50)
    setPolygonVertices([])
  }

  // Calcular escala para visualización
  const getDisplayScale = () => {
    if (!containerRef.current || !imageDimensions.width) return 1
    return containerRef.current.offsetWidth / imageDimensions.width
  }

  const displayScale = getDisplayScale()
  const displayX = planoX * displayScale
  const displayY = planoY * displayScale
  const displayRadius = radius * displayScale
  const displayAncho = ancho * displayScale
  const displayAlto = alto * displayScale

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900">
          🗺️ Mapeador Visual de Plano
        </h4>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-4 py-2 rounded font-medium transition-all"
          style={{
            backgroundColor: isExpanded ? colors.primary : '#f3f4f6',
            color: isExpanded ? '#FFFFFF' : '#374151'
          }}
          onMouseEnter={(e) => {
            if (!isExpanded) {
              e.currentTarget.style.backgroundColor = '#e5e7eb'
            } else {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }
          }}
          onMouseLeave={(e) => {
            if (!isExpanded) {
              e.currentTarget.style.backgroundColor = '#f3f4f6'
            } else {
              e.currentTarget.style.backgroundColor = colors.primary
            }
          }}
        >
          {isExpanded ? '▼ Cerrar' : '▶ Abrir Mapeador'}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          {/* Instrucciones */}
          <div
            className="border rounded-lg p-3 text-sm"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderColor: colors.primary,
              color: colors.primary
            }}
          >
            <p className="font-semibold mb-1">💡 Cómo usar:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Circular:</strong> Haz click en el centro, ajusta radio</li>
              <li><strong>Rectangular:</strong> Haz click en el centro, ajusta ancho y alto</li>
              <li><strong>Polígono:</strong> Marca 4 puntos en diagonal o cualquier orientación</li>
              <li>Las coordenadas se guardan automáticamente</li>
            </ul>
          </div>

          {/* Selector de tipo de forma */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Tipo de Forma
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setShapeType('circular')
                  setPolygonVertices([])
                }}
                className="flex-1 min-w-fit py-2 rounded font-medium transition-all text-white"
                style={{
                  backgroundColor: shapeType === 'circular' ? colors.primary : '#ffffff',
                  color: shapeType === 'circular' ? '#ffffff' : '#374151',
                  border: shapeType === 'circular' ? 'none' : '1px solid #d1d5db'
                }}
                onMouseEnter={(e) => {
                  if (shapeType !== 'circular') e.currentTarget.style.backgroundColor = '#f9fafb'
                }}
                onMouseLeave={(e) => {
                  if (shapeType !== 'circular') e.currentTarget.style.backgroundColor = '#ffffff'
                }}
              >
                ⭕ Circular
              </button>
              <button
                type="button"
                onClick={() => {
                  setShapeType('rectangular')
                  setPolygonVertices([])
                }}
                className="flex-1 min-w-fit py-2 rounded font-medium transition-all text-white"
                style={{
                  backgroundColor: shapeType === 'rectangular' ? colors.primary : '#ffffff',
                  color: shapeType === 'rectangular' ? '#ffffff' : '#374151',
                  border: shapeType === 'rectangular' ? 'none' : '1px solid #d1d5db'
                }}
                onMouseEnter={(e) => {
                  if (shapeType !== 'rectangular') e.currentTarget.style.backgroundColor = '#f9fafb'
                }}
                onMouseLeave={(e) => {
                  if (shapeType !== 'rectangular') e.currentTarget.style.backgroundColor = '#ffffff'
                }}
              >
                ▭ Rectangular
              </button>
              <button
                type="button"
                onClick={() => {
                  setShapeType('polygon')
                  setPlanoX(0)
                  setPlanoY(0)
                }}
                className="flex-1 min-w-fit py-2 rounded font-medium transition-all text-white"
                style={{
                  backgroundColor: shapeType === 'polygon' ? colors.primary : '#ffffff',
                  color: shapeType === 'polygon' ? '#ffffff' : '#374151',
                  border: shapeType === 'polygon' ? 'none' : '1px solid #d1d5db'
                }}
                onMouseEnter={(e) => {
                  if (shapeType !== 'polygon') e.currentTarget.style.backgroundColor = '#f9fafb'
                }}
                onMouseLeave={(e) => {
                  if (shapeType !== 'polygon') e.currentTarget.style.backgroundColor = '#ffffff'
                }}
              >
                🔷 Polígono (4 puntos)
              </button>
            </div>
          </div>

          {/* Canvas interactivo */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Haz click en el plano para marcar la ubicación
            </label>
            <div
              ref={containerRef}
              onClick={handleCanvasClick}
              className="relative w-full bg-white border-2 border-gray-300 rounded-lg overflow-hidden cursor-crosshair"
              style={{ maxWidth: '100%', aspectRatio: `${imageDimensions.width} / ${imageDimensions.height}` }}
            >
              <img
                ref={imgRef}
                src={planoImageUrl}
                alt="Plano del camping"
                onLoad={handleImageLoad}
                className="w-full h-auto"
                draggable={false}
              />

              {/* SVG para dibujar la forma */}
              {imageDimensions.width && (planoX > 0 || planoY > 0 || polygonVertices.length > 0) && (
                <svg
                  className="absolute inset-0 w-full h-full"
                  style={{ width: '100%', height: '100%' }}
                  viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                  preserveAspectRatio="xMidYMid slice"
                >
                  {/* Forma circular */}
                  {shapeType === 'circular' && planoX > 0 && planoY > 0 && (
                    <>
                      <circle
                        cx={planoX}
                        cy={planoY}
                        r={radius}
                        fill="rgba(239, 68, 68, 0.3)"
                        stroke="rgb(239, 68, 68)"
                        strokeWidth="2"
                      />
                      <circle
                        cx={planoX}
                        cy={planoY}
                        r="4"
                        fill="rgb(239, 68, 68)"
                      />
                    </>
                  )}

                  {/* Forma rectangular */}
                  {shapeType === 'rectangular' && planoX > 0 && planoY > 0 && (
                    <>
                      <rect
                        x={planoX - ancho / 2}
                        y={planoY - alto / 2}
                        width={ancho}
                        height={alto}
                        fill="rgba(59, 130, 246, 0.3)"
                        stroke="rgb(59, 130, 246)"
                        strokeWidth="2"
                      />
                      <circle
                        cx={planoX}
                        cy={planoY}
                        r="4"
                        fill="rgb(59, 130, 246)"
                      />
                    </>
                  )}

                  {/* Forma polígono */}
                  {shapeType === 'polygon' && polygonVertices.length > 0 && (
                    <>
                      {polygonVertices.length >= 2 && (
                        <polyline
                          points={polygonVertices.map(v => `${v.x},${v.y}`).join(' ')}
                          fill={polygonVertices.length === 4 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(168, 85, 247, 0.3)'}
                          stroke={polygonVertices.length === 4 ? 'rgb(34, 197, 94)' : 'rgb(168, 85, 247)'}
                          strokeWidth="2"
                        />
                      )}
                      {polygonVertices.length === 4 && (
                        <polyline
                          points={`${polygonVertices[3].x},${polygonVertices[3].y} ${polygonVertices[0].x},${polygonVertices[0].y}`}
                          fill="none"
                          stroke="rgb(34, 197, 94)"
                          strokeWidth="2"
                        />
                      )}
                      {polygonVertices.map((vertex, index) => (
                        <circle
                          key={index}
                          cx={vertex.x}
                          cy={vertex.y}
                          r="5"
                          fill={polygonVertices.length === 4 ? 'rgb(34, 197, 94)' : 'rgb(168, 85, 247)'}
                        />
                      ))}
                      {polygonVertices.length < 4 && (
                        <text
                          x={imageDimensions.width / 2}
                          y={imageDimensions.height / 2}
                          textAnchor="middle"
                          fontSize="16"
                          fill="rgb(168, 85, 247)"
                          fontWeight="bold"
                        >
                          Puntos: {polygonVertices.length}/4
                        </text>
                      )}
                    </>
                  )}
                </svg>
              )}

              {/* Indicador de "sin mapear" */}
              {((shapeType !== 'polygon' && (planoX === 0 || planoY === 0)) ||
                (shapeType === 'polygon' && polygonVertices.length === 0)) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="bg-white px-4 py-2 rounded-lg text-gray-700 font-semibold">
                    {shapeType === 'polygon' ? 'Haz click para marcar los 4 puntos' : 'Haz click para marcar ubicación'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Inputs de coordenadas */}
          {shapeType !== 'polygon' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posición X (píxeles)
                </label>
                <input
                  type="number"
                  min="0"
                  max={imageDimensions.width}
                  value={planoX}
                  onChange={(e) => setPlanoX(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    '--tw-ring-color': colors.primary
                  } as React.CSSProperties}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posición Y (píxeles)
                </label>
                <input
                  type="number"
                  min="0"
                  max={imageDimensions.height}
                  value={planoY}
                  onChange={(e) => setPlanoY(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    '--tw-ring-color': colors.primary
                  } as React.CSSProperties}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                />
              </div>

              {shapeType === 'circular' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Radio (píxeles)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="100"
                    value={radius}
                    onChange={(e) => setRadius(parseInt(e.target.value) || 20)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{
                      '--tw-ring-color': colors.primary
                    } as React.CSSProperties}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                  />
                </div>
              )}

              {shapeType === 'rectangular' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ancho (píxeles)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="200"
                      value={ancho}
                      onChange={(e) => setAncho(parseInt(e.target.value) || 60)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{
                        '--tw-ring-color': colors.primary
                      } as React.CSSProperties}
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alto (píxeles)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="200"
                      value={alto}
                      onChange={(e) => setAlto(parseInt(e.target.value) || 50)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{
                        '--tw-ring-color': colors.primary
                      } as React.CSSProperties}
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div
              className="border rounded-lg p-4"
              style={{
                backgroundColor: `${colors.primary}15`,
                borderColor: colors.primary
              }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Vértices del Polígono ({polygonVertices.length}/4)
              </label>
              {polygonVertices.length > 0 ? (
                <div className="space-y-2">
                  {polygonVertices.map((vertex, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded">
                      <span className="text-sm text-gray-700">
                        Punto {index + 1}: X: {vertex.x}, Y: {vertex.y}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPolygonVertices(polygonVertices.filter((_, i) => i !== index))
                        }}
                        className="ml-2 px-2 py-1 text-xs rounded transition-all"
                        style={{
                          backgroundColor: `${colors.error}15`,
                          color: colors.error
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.error}25`
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.error}15`
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                  {polygonVertices.length === 4 && (
                    <div
                      className="border rounded-lg p-3 mt-3"
                      style={{
                        backgroundColor: `${colors.primary}15`,
                        borderColor: colors.primary
                      }}
                    >
                      <p className="text-sm font-semibold" style={{ color: colors.primary }}>
                        ✅ Polígono completado - {polygonVertices.length} puntos
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Haz click en el plano para agregar puntos</p>
              )}
            </div>
          )}

          {/* Información y acciones */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Espacio:</span>
                <p className="font-semibold text-gray-900">{espacioCodigo}</p>
              </div>
              {(planoX > 0 && planoY > 0) || polygonVertices.length > 0 && (
                <>
                  {shapeType !== 'polygon' ? (
                    <>
                      <div>
                        <span className="text-gray-600">Posición:</span>
                        <p className="font-semibold text-gray-900">X: {planoX}, Y: {planoY}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Tipo:</span>
                        <p className="font-semibold text-gray-900">
                          {shapeType === 'circular' ? '⭕ Circular' : '▭ Rectangular'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Tamaño:</span>
                        <p className="font-semibold text-gray-900">
                          {shapeType === 'circular' ? `R: ${radius}px` : `${ancho}x${alto}px`}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-2">
                      <span className="text-gray-600">Tipo:</span>
                      <p className="font-semibold text-gray-900">🔷 Polígono ({polygonVertices.length}/4 puntos)</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {((planoX > 0 && planoY > 0) || polygonVertices.length > 0) && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full mt-2 px-3 py-2 rounded-lg font-medium transition-all"
                style={{
                  backgroundColor: `${colors.error}15`,
                  color: colors.error
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.error}25`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.error}15`
                }}
              >
                🗑️ Limpiar Mapeador
              </button>
            )}
          </div>
        </div>
      )}

      {/* Vista previa compacta cuando está cerrado */}
      {!isExpanded && ((planoX > 0 && planoY > 0) || polygonVertices.length === 4) && (
        <div
          className="border rounded-lg p-3"
          style={{
            backgroundColor: `${colors.primary}15`,
            borderColor: colors.primary
          }}
        >
          <div className="flex items-center justify-between">
            <div className="text-sm" style={{ color: colors.primary }}>
              <p className="font-semibold">✅ Coordenadas mapeadas</p>
              {shapeType === 'polygon' ? (
                <p className="text-xs">🔷 Polígono con 4 puntos</p>
              ) : (
                <p className="text-xs">X: {planoX}, Y: {planoY} • {shapeType === 'circular' ? `R: ${radius}px` : `${ancho}x${alto}px`}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="px-3 py-1 text-white rounded text-sm transition-all"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              Editar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlanoMapper
